import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchAssetGraph, type GraphNode } from '../../services/assetOperationalApi'
import intelStyles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

const NODE_COLORS: Record<string, string> = {
  ASSET: '#059669',
  INFORMATION_SYSTEM: '#2563eb',
  VULNERABILITY: '#b91c1c',
  CONTROL: '#0891b2',
  RISK: '#9333ea',
  DATA_CATEGORY: '#7c3aed',
  EVIDENCE: '#4b5563',
  REMEDIATION: '#dc2626',
}

const LAYER_OPTIONS = ['systems', 'vulnerabilities', 'controls', 'risks', 'evidence', 'remediation', 'dataCategories']

function layoutNodes(nodes: GraphNode[]) {
  const positioned: Array<GraphNode & { x: number; y: number }> = []
  let y = 40
  nodes.forEach((n, i) => {
    positioned.push({ ...n, x: 40 + (i % 4) * 168, y: y + Math.floor(i / 4) * 72 })
  })
  return positioned
}

interface Props {
  assetId: string | number
}

export default function AssetGraphView({ assetId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [activeLayers, setActiveLayers] = React.useState<string[]>(['systems', 'vulnerabilities', 'controls', 'risks'])
  const [graph, setGraph] = React.useState<Awaited<ReturnType<typeof fetchAssetGraph>> | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!token) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchAssetGraph(token, assetId, orgId, activeLayers.join(','))
      .then((data) => {
        if (!cancelled) setGraph(data)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load graph')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, assetId, orgId, activeLayers.join(',')])

  const positioned = graph ? layoutNodes(graph.nodes) : []

  return (
    <section>
      <div className={intelStyles.graphLayerToggles}>
        {LAYER_OPTIONS.map((layer) => (
          <button
            key={layer}
            type="button"
            className={activeLayers.includes(layer) ? intelStyles.categoryChipActive : intelStyles.categoryChip}
            onClick={() =>
              setActiveLayers((prev) =>
                prev.includes(layer) ? prev.filter((l) => l !== layer) : [...prev, layer]
              )
            }
          >
            {t(`assets.assetPassport.graphLayer.${layer}`, layer)}
          </button>
        ))}
      </div>
      {loading ? <p>{t('common.loading')}</p> : null}
      {error ? <p className="text-red-600 text-sm">{error}</p> : null}
      {!loading && !error && graph ? (
        <div className={intelStyles.graphWrap}>
          <svg width="100%" height={Math.max(320, positioned.length * 20)} viewBox="0 0 720 400" role="img">
            {graph.edges.map((e) => {
              const s = positioned.find((n) => n.id === e.source)
              const tgt = positioned.find((n) => n.id === e.target)
              if (!s || !tgt) return null
              return (
                <line
                  key={e.id}
                  x1={s.x + 60}
                  y1={s.y + 16}
                  x2={tgt.x + 60}
                  y2={tgt.y + 16}
                  stroke="#94a3b8"
                  strokeWidth={1}
                />
              )
            })}
            {positioned.map((n) => (
              <g key={n.id} transform={`translate(${n.x},${n.y})`}>
                <rect width={120} height={32} rx={6} fill={NODE_COLORS[n.type] || '#64748b'} opacity={0.15} />
                <text x={8} y={14} fontSize={10} fill="#334155">
                  {n.type}
                </text>
                <text x={8} y={26} fontSize={11} fontWeight={600} fill="#111827">
                  {(n.label || '').slice(0, 14)}
                </text>
              </g>
            ))}
          </svg>
          <p className="text-xs text-gray-500 mt-2">
            {graph.summary.nodeCount} {t('assets.assetPassport.graphNodes', 'nodes')} · {graph.summary.edgeCount}{' '}
            {t('assets.assetPassport.graphEdges', 'edges')}
          </p>
        </div>
      ) : null}
    </section>
  )
}
